import { LogManager } from '@src/utility/Logger';

import { resolveCaptionLanguageKey } from '../utility/captionLanguage';
import { extractErrorInfo } from '../utility/error';
import { saveVideoResources } from './VideoResourcesProvider';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { CurrentLoadedVideo } from '@src/stores/VideoCreatorStore';
import type { PreparedVideoSave, StoryDTO } from '@src/types.ts/story';

export interface BackgroundSaveResult {
  id: string;
  slug: string;
  hash: string;
  /** The Dato version the save created, when the response carries it. */
  versionId?: string;
  wasNewVideo: boolean;
  originalSnapshotId?: string;
  videoSnapshot: CurrentLoadedVideo;
}

/**
 * Purpose: Save the current video snapshot asynchronously without blocking
 * UI workflows (e.g., aspect ratio switching). This avoids awaiting the
 * heavy network/serialization path and prevents setting `isSaving` on the
 * store, which currently gates other operations (like loading a different
 * aspect ratio variant via `loadVideo`).
 */
export class BackgroundVideoSaveService {
  private logger = LogManager.getLogger('service:BackgroundVideoSave');
  private preparing = new WeakSet<IVideoCreatorStore>();
  private saving = new WeakSet<IVideoCreatorStore>();

  public async prepare(
    store: IVideoCreatorStore,
    withRenderer = true,
    autoSave = false,
    /** Keep the action already on the video (e.g. a publish stamped it). */
    skipLastActionUpdate = false,
  ): Promise<PreparedVideoSave | null> {
    if (this.preparing.has(store)) {
      this.logger.debug('prepare:skip:already-in-flight');
      return null;
    }
    if (
      !store.currentVideo ||
      !store.story ||
      !store.datoClientStore?.videoRepository
    ) {
      this.logger.debug('prepare:skip:missing-deps');
      return null;
    }
    this.preparing.add(store);

    const videoSnapshot = {
      ...store.currentVideo,
      // Aspect ratio change routine will modify this object in-place,
      // those modifications should not be reflected in this snapshot
      extraElementData: { ...store.currentVideo.extraElementData },
    };

    await store.prepareCurrentVideoForSave(withRenderer, videoSnapshot);
    if (!skipLastActionUpdate) {
      store.addVideoAction(
        {
          editor: store.rootStore.userIdentityStore.currentEditor,
          date: new Date().getTime(),
          type: autoSave ? 'autosave' : 'save',
        },
        videoSnapshot,
      );
    }

    const storySnapshot: StoryDTO =
      await store.punchListManager.attachPunchListPhotosToStory();

    const migrationLog =
      store.rootStore.editorStore.transcriptSegmentArchitectureMigrationLog;

    this.preparing.delete(store);

    return {
      videoSnapshot,
      storySnapshot,
      migrationLogStatus: migrationLog?.status,
      migrationLogDebug: migrationLog?.debugInfo,
    };
  }

  public async save(
    prepared: PreparedVideoSave | null,
    store: IVideoCreatorStore,
  ): Promise<BackgroundSaveResult | null> {
    if (!prepared) return null;
    if (this.saving.has(store)) {
      this.logger.debug('save:skip:already-in-flight');
      return null;
    }
    this.saving.add(store);
    try {
      const {
        videoSnapshot,
        storySnapshot,
        migrationLogStatus,
        migrationLogDebug,
      } = prepared;

      if (store.savingError?.isBackground) {
        store.savingError = undefined;
      }

      // PLAT-10788 — karaoke is saved per language (`prepareCurrentVideoForSave`
      // populates `karaokeDataByLanguage`, undefined when empty). Fall back to
      // wrapping the single blob into a one-entry map when the per-language map
      // is missing — shouldn't happen in normal flow, but guards against a logic
      // gap dropping karaoke silently.
      let karaokePayload: Record<string, object> | undefined;
      if (videoSnapshot.karaokeDataByLanguage) {
        karaokePayload = videoSnapshot.karaokeDataByLanguage;
      } else if (videoSnapshot.karaokeData) {
        this.logger.error(
          'save:karaoke-by-language-missing — karaokeData present but karaokeDataByLanguage absent; falling back to single-blob save. This indicates a logic gap in prepareCurrentVideoForSave.',
        );
        const langKey = resolveCaptionLanguageKey(
          videoSnapshot.karaokeData.configs?.get(
            videoSnapshot.karaokeData.defaultConfigId ?? '',
          )?.language,
          undefined,
          storySnapshot.originalVideoLanguage,
        );
        karaokePayload = { [langKey]: videoSnapshot.karaokeData };
      }

      const resourcesMetadata = await saveVideoResources(storySnapshot.id, {
        videoSourceSnapshot: videoSnapshot.videoSource,
        ...(videoSnapshot.transcriptionSnapshot && {
          transcriptionSnapshot: videoSnapshot.transcriptionSnapshot,
        }),
        ...(karaokePayload && { karaokeData: karaokePayload }),
        ...(videoSnapshot.scenes && { scenes: videoSnapshot.scenes }),
      });

      if (
        !resourcesMetadata ||
        Object.entries(resourcesMetadata).some(([, v]) => !v)
      ) {
        this.logger.error('save:resources-metadata-invalid', {
          resourcesMetadata,
        });
        // ensures that RetrySavingModal gets opened
        throw new Error('Failed to save video resources metadata');
      }
      if (!resourcesMetadata.videoSourceSnapshot) {
        this.logger.error('save:video-source-snapshot-missing');
        // ensures that RetrySavingModal gets opened
        throw new Error('Failed to save video source snapshot metadata');
      }

      const existingTranscriptMeta =
        videoSnapshot.resourcesMetadata?.transcriptionSnapshot;

      const payload = {
        ...videoSnapshot,
        resourcesMetadata: {
          videoSourceSnapshot: resourcesMetadata.videoSourceSnapshot,
          ...(resourcesMetadata.transcriptionSnapshot && {
            transcriptionSnapshot: resourcesMetadata.transcriptionSnapshot,
          }),
          ...(!resourcesMetadata.transcriptionSnapshot &&
            existingTranscriptMeta && {
              transcriptionSnapshot: existingTranscriptMeta,
            }),
          ...(resourcesMetadata.karaokeData && {
            karaokeData: resourcesMetadata.karaokeData,
          }),
          ...(resourcesMetadata.scenes && {
            scenes: resourcesMetadata.scenes,
          }),
        },
        ...(resourcesMetadata.transcriptionSnapshot && {
          transcriptionChanges: [],
        }),
        transcriptionMigrationStatus: migrationLogStatus,
        transcriptionMigrationLog: migrationLogDebug,
      };
      const videoResult =
        await store.datoClientStore.videoRepository.saveOrUpdateVideo(payload);
      this.logger.debug('save:video-success', { id: videoResult.id });

      videoSnapshot.resourcesMetadata = payload.resourcesMetadata;

      const wasNewVideo = !videoSnapshot.id;
      const originalSnapshotId = videoSnapshot.id;

      if (wasNewVideo) {
        videoSnapshot.id = videoResult.id;
        if (videoSnapshot.parentVideo) {
          await store.addCurrentToParentVideos(videoSnapshot);
        } else {
          await store.addCurrentToStoryVideos(videoSnapshot);
        }
      }

      const storyResult = await store.updateStory(storySnapshot);
      this.logger.debug('save:story-success', { id: storyResult.id });

      return {
        id: videoResult.id,
        slug: videoResult.slug,
        hash: videoResult.hash,
        versionId: videoResult.versionId,
        wasNewVideo,
        originalSnapshotId,
        videoSnapshot,
      };
    } catch (e) {
      this.logger.warn('save:failed', extractErrorInfo(e));
      store.savingError = e as IVideoCreatorStore['savingError'];
      if (store.savingError) {
        store.savingError.isBackground = true;
        store.savingError.preparedVideoSave = prepared;
      }
      return null;
    } finally {
      this.saving.delete(store);
    }
  }
}

// Singleton export for convenience (lightweight service)
export const backgroundVideoSaveService = new BackgroundVideoSaveService();
